// Polling controller for the desktop Player page when the backend queues cold
// player season/game-log cache work and returns an explicit "processing" state.
// Pure and injectable (fetch + sleep + cancel signal) so it is fully testable
// without React or real timers.

export const PLAYER_POLL = {
  minDelayMs: 2000,   // clamp floor for retry delay
  maxDelayMs: 15000,  // clamp ceiling for retry delay
  defaultDelayMs: 4000, // used when the backend does not supply retry_after
  maxAttempts: 12,    // bound total polls
  maxTotalMs: 90000,  // bound total wall-clock time
};

// Backend tokens that mean "cold cache work is in flight".
const PROCESSING_TOKENS = new Set([
  "processing", "queued", "in_progress", "in-progress", "building", "caching", "warming", "pending",
]);

export function isProcessingState(state) {
  return state === "processing";
}

// A coverage payload is "processing" only when work is in flight AND no data is
// populated yet. If stats/log are present we treat it as populated (stop).
export function coverageIsProcessing(coverage) {
  const c = coverage || {};
  const token = String(c.unavailable_reason ?? c.status ?? c.state ?? "").toLowerCase();
  const populated = c.has_season_stats === true || c.has_game_log === true;
  return !populated && PROCESSING_TOKENS.has(token);
}

// Read a retry_after hint (seconds) from either the payload or its coverage.
export function readRetryAfter(raw) {
  const d = raw || {};
  const c = d.coverage || {};
  return d.retry_after ?? d.retry_after_seconds ?? c.retry_after ?? c.retry_after_seconds ?? null;
}

// Clamp a backend retry_after (seconds) into a sane 2–15s window (ms).
export function clampRetryDelay(retryAfterSeconds, cfg = PLAYER_POLL) {
  const n = Number(retryAfterSeconds);
  const ms = Number.isFinite(n) && n > 0 ? n * 1000 : cfg.defaultDelayMs;
  return Math.min(cfg.maxDelayMs, Math.max(cfg.minDelayMs, ms));
}

// Pure decision: given the latest result + progress, decide the next action.
export function planPlayerPoll({ state, attempts, elapsedMs, retryAfter, cfg = PLAYER_POLL }) {
  if (!isProcessingState(state)) return { done: true, timedOut: false };
  if (attempts >= cfg.maxAttempts || elapsedMs >= cfg.maxTotalMs) return { done: true, timedOut: true };
  return { done: false, timedOut: false, delayMs: clampRetryDelay(retryAfter, cfg) };
}

// Async polling loop. Polls ONLY while the result state is "processing", clamps
// the retry delay, bounds attempts/total time, and cancels on an aborted signal
// (unmount/navigation). Resolves { result, timedOut, cancelled, attempts }.
export async function runPlayerPolling({ fetchPlayer, signal, cfg = PLAYER_POLL, now = () => Date.now(), sleep } = {}) {
  const start = now();
  let attempts = 0;
  const doSleep = sleep || ((ms) => new Promise((r) => setTimeout(r, ms)));

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal && signal.aborted) return { result: null, timedOut: false, cancelled: true, attempts };

    const result = await fetchPlayer();
    attempts += 1;
    if (signal && signal.aborted) return { result, timedOut: false, cancelled: true, attempts };

    const plan = planPlayerPoll({
      state: result && result.state,
      attempts,
      elapsedMs: now() - start,
      retryAfter: result && result.retryAfter,
      cfg,
    });
    if (plan.done) return { result, timedOut: plan.timedOut, cancelled: false, attempts };

    await doSleep(plan.delayMs, signal);
    if (signal && signal.aborted) return { result, timedOut: false, cancelled: true, attempts };
  }
}
